import { useMemo } from 'react';

import { useFeatureFlags, useLanguage } from '../../hooks';
import { useLinkedAccountsQuery } from '../../queries/accountSwitches';
import { buildSwitcherEntries, SwitcherEntry } from './accountSwitcherLabels';

export interface SwitcherEntries {
  /** Every account the person can stand on, the current one included. */
  entries: SwitcherEntry[];
  currentUserId: number | null;
  /** What the "see all accounts (N)" counter reports. */
  total: number;
  /** The first fetch of a page load. False when the feature gates the request. */
  isLoading: boolean;
}

/**
 * The selector rows, resolved once for the menu and the full list.
 *
 * Both views need the same labels, and the name tie-break only works over the
 * whole set: resolved separately, a modal listing every account and a menu
 * listing five would disagree on which rows carry their id.
 *
 * Without the feature there is nothing to switch to, and the request would be
 * paid by every user on every page load for an answer that is always empty.
 */
export function useSwitcherEntries(): SwitcherEntries {
  const lang = useLanguage();
  const { checkFlag } = useFeatureFlags();
  const { data, isLoading } = useLinkedAccountsQuery(checkFlag('Multicuenta'));

  const accounts = data?.accounts;
  const current = data?.current ?? null;
  const unnamedAccount = lang.mainTopBar.accountSwitcher.unnamedAccount;

  const entries = useMemo(() => {
    if (!accounts?.length) return [];

    // The current account goes into the same list so the name tie-break counts
    // it: if it shares a name with one of the others, both rows carry their id.
    return buildSwitcherEntries(current ? [current, ...accounts] : accounts, (id) =>
      unnamedAccount.replace('{{id}}', String(id)),
    );
  }, [accounts, current, unnamedAccount]);

  return {
    entries,
    currentUserId: current?.user_id ?? null,
    total: entries.length,
    isLoading,
  };
}
